import { api } from '../api/client'
import type { Assessment, AssessmentQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useMemo, useState } from 'react'
import { useListParams } from '../hooks/useListParams'
import { useEndlessList } from '../hooks/useEndlessList'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed } from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Refs } from '../components/common/Refs'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import s from './AssessmentsPage.module.scss'

/**
 * Die Ampel — was ein Agent zuletzt zu etwas gesagt hat.
 *
 * NUR LESEND. Eingetragen wird über den MCP-Zugang: Die Einschätzung ist die
 * Arbeit eines Laufs, der stündlich prüft; die Oberfläche zeigt sie an.
 *
 * EINE ZEITREIHE, keine Bestandsliste. Deshalb absteigend nach dem Zeitpunkt,
 * an dem sie galt — der aktuelle Stand ist die Frage, nicht der erste Eintrag.
 */

const PERMISSION = 'assessment:read'
const PAGE_SIZE = 50

/** Wie viele Zeilen ohne Scrollen stehen bleiben — wie bei den Meldungen. */
const SICHTBARE_ZEILEN = 10

const zeitpunkt = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short', timeStyle: 'short',
})

const STUFEN: Record<Assessment['rating'], string> = {
  GREEN: 'grün', AMBER: 'gelb', RED: 'rot',
}

/**
 * Die Stufe als Punkt mit Wort.
 *
 * NICHT NUR FARBE: Wer Rot und Grün nicht unterscheidet, sähe sonst drei
 * gleiche Punkte. Das Wort daneben kostet wenig und trägt die Aussage allein.
 */
function Ampel({ wert }: { wert: Assessment['rating'] }) {
  return (
    <span className={s.ampel}>
      <span className={`${s.punkt} ${s[wert.toLowerCase()]}`} aria-hidden="true" />
      {STUFEN[wert]}
    </span>
  )
}

/**
 * Wann zuletzt hingesehen wurde.
 *
 * Eine grüne Ampel von vor drei Wochen ist etwas anderes als eine, die vor
 * einer Stunde bestätigt wurde — und ohne diese Spalte sähen beide gleich aus.
 * Fehlt die Bestätigung, gilt der Zeitpunkt des Eintrags: Dann wurde seither
 * nicht mehr geprüft.
 */
function Geprueft({ zeile }: { zeile: Assessment }) {
  if (!zeile.confirmed_at) {
    return <span className={s.nie}>—</span>
  }
  return <>{zeitpunkt.format(new Date(zeile.confirmed_at))}</>
}

const COLUMNS: Column<Assessment>[] = [
  { key: 'assessed_at', label: 'Stand', sortable: true, mono: true,
    minWidth: '128px',
    render: (a) => zeitpunkt.format(new Date(a.assessed_at)),
    help: 'Wann die Einschätzung GALT — nicht, wann sie eingetragen wurde. Die beiden gehen auseinander, wenn jemand eine Lage von gestern nachträgt.' },
  { key: 'targets', label: 'Betrifft', minWidth: '200px',
    // „Markt“ statt einer leeren Zelle: Die Einschätzung gilt der Lage, nicht
    // einem Titel — das ist eine Aussage und kein fehlender Wert.
    render: (a) => <Refs refs={a.targets} leer="Markt" />,
    help: 'Instrumente, Rohstoffe oder Branchen — mehrere zugleich sind möglich und der eigentliche Zweck: „Öl zieht an, und mit ihm die Förderer“ ist EINE Einschätzung zu drei Zielen. „Markt“ heißt: ohne Bezug zu einem einzelnen Papier.' },
  { key: 'rating', label: 'Ampel', sortable: true, minWidth: '96px',
    render: (a) => <Ampel wert={a.rating} />,
    help: 'Drei Stufen. Der Zwischenton gehört in die Einschätzung daneben, nicht in eine vierte Farbe.' },
  { key: 'headline', label: 'Einschätzung', minWidth: '340px', wrap: true,
    help: 'Die Aussage in einer Zeile. Pflichtfeld — eine Ampel ohne Begründung ist eine Farbe.' },
  { key: 'confirmed_at', label: 'Zuletzt geprüft', sortable: false, mono: true,
    align: 'right', minWidth: '128px', hideOn: 'tablet',
    render: (a) => <Geprueft zeile={a} />,
    help: 'Wann zuletzt nachgesehen und bestätigt wurde, ohne dass sich etwas änderte. Ein Strich heißt: seit dem Eintrag nicht mehr geprüft — eine grüne Ampel von vor drei Wochen ist etwas anderes als eine von vor einer Stunde.' },
]

/**
 * Der heutige Tag als JJJJ-MM-TT — in ORTSZEIT.
 *
 * Nicht ueber `toISOString`: Das liefert UTC, und wer um halb eins nachts in
 * Deutschland auf die Seite geht, bekaeme den Vortag als "heute".
 */
function heutigerTag(): string {
  const jetzt = new Date()
  const zweistellig = (n: number) => String(n).padStart(2, '0')
  return `${jetzt.getFullYear()}-${zweistellig(jetzt.getMonth() + 1)}`
       + `-${zweistellig(jetzt.getDate())}`
}

/**
 * Anfang und Ende eines Tages als Zeitpunkt — ebenfalls in Ortszeit.
 *
 * `new Date(jahr, monat, tag, 0, 0, 0)` baut die Ortszeit, `toISOString`
 * rechnet sie in den passenden UTC-Zeitpunkt um. Ein fest angehaengtes "Z"
 * waere zwei Stunden daneben: Mitternacht in Berlin ist 22 Uhr UTC am
 * Vortag, und der Filter zeigte Eintraege des falschen Tages.
 */
function tagesgrenze(datum: string, ende: boolean): string {
  const [jahr, monat, tag] = datum.split('-').map(Number)
  return ende
    ? new Date(jahr, monat - 1, tag, 23, 59, 59, 999).toISOString()
    : new Date(jahr, monat - 1, tag, 0, 0, 0, 0).toISOString()
}

const tagesbeginn = (datum: string) => tagesgrenze(datum, false)
const tagesende = (datum: string) => tagesgrenze(datum, true)

export function AssessmentsPage() {
  const allowed = useHasPermission(PERMISSION)

  // Der HEUTIGE Tag als Vorbelegung — von und bis derselbe.
  //
  // Eine Ampel ist ein Stand, kein Vorgang: Was gestern galt, ist heute
  // Geschichte. Ohne Vorbelegung stand hier die ganze Zeitreihe, und der
  // heutige Stand — die eigentliche Frage — musste erst herausgefiltert
  // werden.
  //
  // EINMAL gerechnet, sonst wanderte das Fenster um Mitternacht mitten in
  // der Sitzung. Dieselbe Ueberlegung wie bei den Ereignissen
  // (docs/adr/0002).
  const heute = useMemo(() => heutigerTag(), [])

  // Welche Zeile aufgeklappt ist, und wie viele stehen bleiben. Beides im
  // Zustand und nicht in der Adresse: Sichteinstellungen dieses Augenblicks,
  // nichts zum Teilen — wie bei den Meldungen.
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  const [sichtbar, setSichtbar] = useState<number | null>(SICHTBARE_ZEILEN)

  const list = useListParams({
    // `ref` und nicht mehr `bezug`: Dieselbe Sprache wie die Meldungen und
    // wie der Sprung „Ampel ↗" aus dem Popover, der mit `?ref=` ankommt.
    filters: { rating: '', ref: '', von: '', bis: '' },
    // Ueber `preset` und nicht als statische Vorgabe: Nur so laesst sich das
    // Fenster abschuetteln — wer ein Datumsfeld leert, schreibt ein Minus in
    // die Adresse, und die Vorbelegung bleibt weg.
    preset: { von: heute, bis: heute },
    sort: 'assessed_at',
    // ABSTEIGEND als Vorgabe, anders als bei den übrigen Listen: Eine
    // Zeitreihe wird von hinten gelesen.
    descending: true,
  })

  const params: AssessmentQuery = {
    rating: list.filters.rating ? [list.filters.rating] : undefined,
    // Die Gesamtlage ist ein Ziel wie jedes andere — kein Sonderschalter
    // mehr, sondern derselbe `ref`, den auch ein Papier benutzt.
    ref: list.filters.ref || undefined,
    // Das Feld liefert einen TAG, der Endpunkt erwartet einen ZEITPUNKT.
    // Ohne Uhrzeit waere "bis heute" Mitternacht und schnitte genau den Tag
    // ab, den es meint.
    since: list.filters.von ? tagesbeginn(list.filters.von) : undefined,
    until: list.filters.bis ? tagesende(list.filters.bis) : undefined,
    sort: list.sort as AssessmentQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    size: PAGE_SIZE,
    total: true,
  }

  // ENDLOS STATT SEITENWEISE, wie bei Meldungen und Ereignissen: Die Tabelle
  // steht fest und waechst beim Scrollen. Ein Blaetterwerk daneben waere ein
  // zweiter Weg durch dieselben Zeilen.
  const { rows: zeilen, loading, loadingMore, error, denied, loadMore, total }
    = useEndlessList(api.assessments, params, allowed)

  // Ob ueberhaupt ein Zeitraum wirkt — fuer die Leermeldung.
  //
  // NICHT ueber `touched`: Die Vorbelegung IST der Ausgangszustand, also gilt
  // die Ansicht als unberuehrt, obwohl ein Filter greift.
  //
  // DREI VERSCHIEDENE LEEREN, und sie bedeuten Verschiedenes. Bisher stand
  // bei jedem "Ein Agent traegt sie ein, solange keiner gelaufen ist, bleibt
  // die Liste leer" — mit dem Tagesfenster wurde daraus eine Falschaussage:
  // Es GIBT Einschaetzungen, nur keine von heute.
  const zeitraum = Boolean(list.filters.von || list.filters.bis)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Einschätzungen" />
  }

  const offen = zeilen.find((z) => z.id === gewaehlt) ?? null

  return (
    <>
      <FilterBar>
        <FilterFixed>
          <Select label="Ampel" value={list.filters.rating}
                  onChange={(event) =>
                    list.setFilter('rating', event.target.value)}>
            <option value="">alle Stufen</option>
            <option value="GREEN">grün</option>
            <option value="AMBER">gelb</option>
            <option value="RED">rot</option>
          </Select>
        </FilterFixed>
        <FilterFixed>
          {/*
            „Markt“ ist kein Ziel unter vielen, sondern die Abwesenheit eines
            Ziels — deshalb ein eigener Filter und keine Zeile in der
            Zielauswahl. Ein leeres Feld hieße dort „alle“.
          */}
          <Select label="Bezug" value={list.filters.ref}
                  onChange={(event) =>
                    list.setFilter('ref', event.target.value)}>
            <option value="">alle</option>
            <option value="mkt:IW:GLOBAL">nur allgemeine Marktlage</option>
            {/*
              Ein Verweis aus dem Popover — ein Titel, ein Land, ein
              Rohstoff — steht nicht in der Auswahl. Ohne diesen Eintrag
              zeigte das Feld „alle", waehrend die Liste gefiltert ist.
            */}
            {list.filters.ref && list.filters.ref !== 'mkt:IW:GLOBAL' && (
              <option value={list.filters.ref}>{list.filters.ref}</option>
            )}
          </Select>
        </FilterFixed>
        {/*
          Der Zeitraum bezieht sich auf den STAND — die Spalte, nach der die
          Liste ohnehin sortiert ist. Nicht auf „zuletzt geprüft": Das ist
          eine Bestätigung, kein neuer Stand.
        */}
        <FilterFixed>
          <Input label="Von" type="date" value={list.filters.von}
                 title="Stand ab diesem Tag"
                 onChange={(event) =>
                   list.setFilter('von', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Bis" type="date" value={list.filters.bis}
                 title="Stand bis zu diesem Tag, einschließlich"
                 onChange={(event) =>
                   list.setFilter('bis', event.target.value)} />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        <DataTable
          columns={COLUMNS}
          rows={zeilen}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          // KEIN Pager: Die Liste waechst beim Scrollen.
          visibleRows={sichtbar}
          onVisibleRows={setSichtbar}
          onEndReached={loadMore}
          loadingMore={loadingMore}
          status={total !== null && zeilen.length < total
            ? `${zeilen.length} von ${total} Einschätzungen`
            : `${zeilen.length} ${zeilen.length === 1
                ? 'Einschätzung' : 'Einschätzungen'}`}
          // Die Zeile klappt den Satz auf. In der Zelle steht nur die
          // Ueberschrift; die Begruendung hat im Bestand im Schnitt 1.454
          // Zeichen und stand bisher NIRGENDS in der Oberflaeche.
          onRowClick={(row) =>
            setGewaehlt(gewaehlt === row.id ? null : row.id)}
          // Die aufgeklappte Zeile markieren: Der Detailbereich steht
          // UNTERHALB der Tabelle und bei einer langen Liste womoeglich
          // ausser Sichtweite der Zeile, zu der er gehoert.
          rowAttrs={(row): Record<string, string> =>
            row.id === gewaehlt ? { 'data-active': 'true' } : {}}
          loading={loading}
          empty={<EmptyState title={zeitraum
            ? 'an diesen Tagen nichts eingetragen'
            : 'keine Einschätzungen'}>
            {zeitraum
              ? 'Die Ansicht öffnet mit dem heutigen Tag. Für ältere Stände '
                + 'den Zeitraum erweitern oder die Datumsfelder leeren.'
              : list.touched
              ? 'Filter zurücksetzen.'
              : 'Ein Agent trägt sie über den MCP-Zugang ein. Solange keiner '
                + 'gelaufen ist, bleibt die Liste leer.'}
          </EmptyState>}
        />
      </Card>

      {/*
        Der volle Satz UNTERHALB der Tabelle, nicht daneben — wie bei den
        Meldungen: Die Tabelle nutzt die ganze Breite, und ein Seitenbereich
        zwaenge sie in die Haelfte.
      */}
      {offen && <AssessmentDetail item={offen} />}
    </>
  )
}

/**
 * Die aufgeklappte Einschaetzung.
 *
 * Was in der Zelle gekuerzt steht, steht hier ganz — vor allem die
 * Begruendung: Fakten, Chancen, Risiken, Ausloeser, Kipp-Bedingungen,
 * Quellen. Sie ist der eigentliche Inhalt; die Ueberschrift ist nur die
 * Zeile darueber.
 */
function AssessmentDetail({ item }: { item: Assessment }) {
  return (
    <Card span={12}>
      <div className={s.detailKopf}>
        <h2 className={s.detailTitel}>{item.headline}</h2>
        <Ampel wert={item.rating} />
      </div>

      <div className={s.detailMeta}>
        <span>Stand {zeitpunkt.format(new Date(item.assessed_at))}</span>
        {/* Ein Strich, kein Datum: "seit dem Eintrag nicht mehr geprueft"
            ist eine Aussage, kein fehlender Wert. */}
        <span>
          zuletzt geprüft {item.confirmed_at
            ? zeitpunkt.format(new Date(item.confirmed_at))
            : <span className={s.nie}>—</span>}
        </span>
        <span>Quelle {item.source}</span>
        <span>erfasst {zeitpunkt.format(new Date(item.created))}</span>
      </div>

      {item.note
        // `pre-line`: Absaetze in der Begruendung bleiben Absaetze.
        ? <p className={s.detailText}>{item.note}</p>
        : <p className={s.detailLeer}>Ohne Begründung eingetragen.</p>}

      <div className={s.detailBezug}>
        <span className={s.detailBezugTitel}>Betrifft</span>
        {/* Ohne Obergrenze, anders als in der Zeile: Hier ist Platz. */}
        <Refs refs={item.targets} leer="Markt" />
      </div>
    </Card>
  )
}
